import { AgGrid } from "@jsq/ds/core";
import { DefaultColumnTypes } from "#utils/agGrid.constants";
import Money from "../../Money";
import { DEFAULT_BASE_MONEY_PROPS } from "../../Money/constants";
import { type MoneyType } from "../../Money/types";
import { BankAccountCell } from "./components/AccountLabel";
import { DeltaCell } from "./components/DeltaCell";
import { GroupCell } from "./components/GroupCell";
import { type CashAccountRowItem, type GroupBy } from "./types";

/**
 * Columns supported by the accounts grid. Columns visible and grouped differ per
 * group-by mode — see `getColumnDefinitions(groupBy)`.
 */
export enum SupportedColumns {
  Collection = "collectionName",
  Entity = "entityName",
  Bank = "bankName",
  BankAccount = "bankAccountName",
  Balance = "balance",
  Delta = "delta",
}

/**
 * Custom AG Grid aggregation for `MoneyType` columns. A plain `'sum'` can't sum
 * the `{ amount, currency, precision }` object, so this sums the **minor units**
 * (`amount`) across children and returns a `MoneyType` — the value the `Money`
 * cell renderer and the group sort read. Currency/precision are taken from the
 * first non-empty child (M1 is USD-only). Children may be leaf `MoneyType`s or
 * already-aggregated `MoneyType`s (AG Grid aggregates bottom-up).
 */
export const sumMoneyAggFunc = (params: Pick<AgGrid.IAggFuncParams, "values">): MoneyType => {
  const monies = (params.values ?? []).filter(
    (v): v is MoneyType => v != null && typeof v === "object" && "amount" in v,
  );
  if (monies.length === 0) {
    return DEFAULT_BASE_MONEY_PROPS;
  }
  const amount = monies.reduce((sum, m) => sum + (m.amount ?? 0), 0);
  return {
    amount,
    currency: monies[0].currency,
    precision: monies[0].precision,
  };
};

// Balance/Delta are `MoneyType` objects and aren't part of search — quick filter
// matches on names only. Returning "" keeps them out of the filter cache (an
// unset getter would stringify the object to "[object Object]", which both fails
// real searches and false-matches a query for "object").
const excludeFromQuickFilter = (): string => "";

const collectionColumn: AgGrid.ColDef<CashAccountRowItem> = {
  field: SupportedColumns.Collection,
  headerName: "Collection",
  filter: "agSetColumnFilter",
  // Collection-less entities have a null collectionName — show a "--" placeholder.
  valueFormatter: ({ value }) => (value as string | null) ?? "--",
};

const entityColumn: AgGrid.ColDef<CashAccountRowItem> = {
  field: SupportedColumns.Entity,
  headerName: "Entity",
  filter: "agSetColumnFilter",
};

const bankColumn: AgGrid.ColDef<CashAccountRowItem> = {
  field: SupportedColumns.Bank,
  headerName: "Bank",
  filter: "agSetColumnFilter",
};

const bankAccountColumn: AgGrid.ColDef<CashAccountRowItem> = {
  field: SupportedColumns.BankAccount,
  headerName: "Bank account",
  filter: "agSetColumnFilter",
  cellRenderer: BankAccountCell,
};

const balanceColumn = (): AgGrid.ColDef<CashAccountRowItem> => ({
  field: SupportedColumns.Balance,
  headerName: "Balance",
  type: DefaultColumnTypes.numericColumn,
  headerClass: "ag-left-aligned-header",
  filter: "agNumberColumnFilter",
  // Divides minor units by `10^precision` so `agNumberColumnFilter`'s range
  // works on real amounts.
  filterValueGetter: (params: AgGrid.ValueGetterParams<CashAccountRowItem>): number => {
    const amount = params?.data?.balance?.amount ?? 0;
    const precision = params?.data?.balance?.precision ?? 0;
    return amount / Math.pow(10, precision);
  },
  getQuickFilterText: excludeFromQuickFilter,
  aggFunc: sumMoneyAggFunc,
  cellRenderer: Money,
  cellStyle: {
    flexDirection: "row-reverse",
  },
  cellRendererParams: (params: AgGrid.ICellRendererParams<CashAccountRowItem>) => ({
    // On group rows `data` is undefined; the aggregated value lives on the node.
    money: (params.data?.balance ?? params.value) as MoneyType | undefined,
    rounded: false,
  }),
});

const deltaColumn = (deltaHeader: string): AgGrid.ColDef<CashAccountRowItem> => ({
  field: SupportedColumns.Delta,
  headerName: deltaHeader,
  type: DefaultColumnTypes.numericColumn,
  filter: false,
  // Last column — not resizable and not sortable.
  resizable: false,
  sortable: false,
  aggFunc: sumMoneyAggFunc,
  getQuickFilterText: excludeFromQuickFilter,
  cellStyle: {
    flexDirection: "row-reverse",
  },
  // Two-line design: colored delta amount + % change over the prior balance.
  cellRenderer: DeltaCell,
});

/**
 * Builds the AG Grid column definitions for the active group-by mode.
 *
 * | Group by | columns (the grouped col carries `rowGroup: true`) |
 * | Entity   | Entity(rowGroup) · Balance · Delta                  |
 * | Bank     | Bank(rowGroup) · Entity · Collection · Balance · Delta |
 * | Account  | Bank account · Entity · Collection · Balance · Delta (flat) |
 *
 * The grouped column is hidden (its values surface through `autoGroupColumnDef`).
 *
 * @param groupBy - the active grouping mode
 * @param deltaHeader - dynamic delta label off the date range (defaults to "Delta")
 */
export const getColumnDefinitions = (
  groupBy: GroupBy,
  deltaHeader = "Delta",
): AgGrid.ColDef<CashAccountRowItem>[] => {
  switch (groupBy) {
    case "ENTITY":
      // Hierarchy: Collection → Entity → bank accounts. `groupAllowUnbalanced`
      // (set on the grid) lets a collection-less entity sit at the top level
      // alongside Collection groups, so the group renderer labels each node
      // "Collection …" or "Entity …" per its grouping column.
      return [
        { ...collectionColumn, rowGroup: true, rowGroupIndex: 0, hide: true, sort: "asc" },
        { ...entityColumn, rowGroup: true, rowGroupIndex: 1, hide: true, sort: "asc" },
        balanceColumn(),
        deltaColumn(deltaHeader),
      ];
    case "BANK":
      // Group by bank only; Entity + Collection are plain columns. `rowGroup`/
      // `hide` are set explicitly (not omitted) so switching out of Entity mode
      // clears its grouping — AG Grid preserves omitted colDef state on update.
      return [
        { ...bankColumn, rowGroup: true, hide: true, sort: "asc" },
        { ...entityColumn, rowGroup: false, hide: false, sort: "asc" },
        { ...collectionColumn, rowGroup: false, hide: false },
        balanceColumn(),
        deltaColumn(deltaHeader),
      ];
    case "ACCOUNT":
    default:
      // Flat (no grouping). Explicit `rowGroup: false`/`hide: false` for the same
      // reason as BANK — reset any grouping carried over from Entity mode.
      return [
        bankAccountColumn,
        { ...entityColumn, rowGroup: false, hide: false, sort: "asc" },
        { ...collectionColumn, rowGroup: false, hide: false },
        balanceColumn(),
        deltaColumn(deltaHeader),
      ];
  }
};

/** Column defaults applied to every column in the grid. */
export const defaultColDef: AgGrid.ColDef<CashAccountRowItem> = {
  flex: 1,
  sortable: true,
  resizable: true,
  cellStyle: {
    display: "flex",
    alignItems: "center",
  },
  menuTabs: ["filterMenuTab"],
};

/** Default page size for the accounts grid; must be one of {@link PAGE_SIZE_OPTIONS}. */
export const DEFAULT_PAGE_SIZE = 20;
/** Page-size choices offered in the pagination panel. */
export const PAGE_SIZE_OPTIONS: readonly number[] = [10, 20, 50, 100];

/** Grid-level options shared across group-by modes. */
export const gridOptions: AgGrid.GridOptions<CashAccountRowItem> = {
  suppressCellFocus: true,
  // Show plain column headers ("Balance") instead of AG Grid's default
  // aggregation-wrapped headers ("func(Balance)") on aggregated columns.
  suppressAggFuncInHeader: true,
  rowHeight: 67,
  // Collections + standalone entities render at the same level.
  groupAllowUnbalanced: true,
  groupDefaultExpanded: 0,
  // Quick-filter search spans hidden columns too, so the grouped column (entity
  // in Entity mode, bank in Bank mode) stays searchable regardless of group-by.
  includeHiddenColumnsInQuickFilter: true,
  // Client-side pagination over top-level rows — groups stay intact (their leaf
  // rows aren't split across pages). The starting page size is a default; the
  // live page + size are seeded from / synced to the view (see index.tsx).
  pagination: true,
  paginationPageSize: DEFAULT_PAGE_SIZE,
  paginationPageSizeSelector: [...PAGE_SIZE_OPTIONS],
  paginateChildRows: false,
  suppressMenuHide: true,
};

/** The auto-group column header differs per group-by mode. */
const AUTO_GROUP_HEADER: Record<GroupBy, string> = {
  ENTITY: "Entity",
  BANK: "Bank",
  ACCOUNT: "Bank account",
};

/**
 * The auto-group (tree) column def for the active group-by mode. Leaf
 * (bank-account) rows surface through this column, and `GroupCell` renders both
 * the group label + count and the leaf label.
 */
export const getAutoGroupColumnDef = (groupBy: GroupBy): AgGrid.ColDef<CashAccountRowItem> => ({
  headerName: AUTO_GROUP_HEADER[groupBy],
  field: SupportedColumns.BankAccount,
  // Delegates to whichever column is currently row-grouped (Collection/Entity in
  // ENTITY mode, Bank in BANK mode) and its `agSetColumnFilter`, so selecting a
  // value filters the tree down to just the matching groups + their leafs.
  filter: "agGroupColumnFilter",
  // Suppress AG Grid's native "(N)" group count; our renderer shows the label
  // + bank-account count instead.
  cellRendererParams: {
    suppressCount: true,
    innerRenderer: GroupCell,
  },
});
